import { selectCurrencyData } from 'legacy/lut/features/partner360/configuration'
import { selectAvailableCurrencies } from 'legacy/lut/features/partner360/configuration/selectors/selectAvailableCurrencies'
import { useEffect, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import { getUrlParam } from 'core-components/helpers/url'
import { LOCAL_CURRENCY_URL_SEGMENT } from 'partner360/configs'

import { useCurrencies } from './useCurrencies'

const normalizeCurrencyValue = (value?: string | null) => value?.trim().toUpperCase() || null

export const buildRouteCurrencyPathSegment = (currency?: string | null) => {
  const normalizedCurrency = currency?.trim().toLowerCase()

  return normalizedCurrency
    ? `/${LOCAL_CURRENCY_URL_SEGMENT}/${encodeURIComponent(normalizedCurrency)}`
    : ''
}

export const resolveRouteCurrency = ({
  routeCurrency,
  availableCurrencies,
  localCurrency,
  defaultCurrency,
}: {
  routeCurrency?: string | null
  availableCurrencies?: string[]
  localCurrency?: string | null
  defaultCurrency?: string | null
}) => {
  const normalizedRouteCurrency = normalizeCurrencyValue(routeCurrency)

  if (!normalizedRouteCurrency) {
    return null
  }

  const allowedCurrencies = new Set(
    ['USD', ...(availableCurrencies ?? []), localCurrency ?? null, defaultCurrency ?? null]
      .map(normalizeCurrencyValue)
      .filter(Boolean)
  )

  return allowedCurrencies.has(normalizedRouteCurrency) ? normalizedRouteCurrency : null
}

export const useRouteCurrencySync = () => {
  const location = useLocation()
  const { localCurrency, setCurrency } = useCurrencies()
  const availableCurrencies = useSelector(selectAvailableCurrencies)
  const { defaultCurrency } = useSelector(selectCurrencyData)
  const prevRouteCurrencyRef = useRef<string | null>(null)

  const routeCurrency = useMemo(
    () =>
      resolveRouteCurrency({
        routeCurrency: getUrlParam(LOCAL_CURRENCY_URL_SEGMENT, location.pathname),
        availableCurrencies,
        localCurrency,
        defaultCurrency,
      }),
    [availableCurrencies, defaultCurrency, localCurrency, location.pathname]
  )

  useEffect(() => {
    if (!routeCurrency || routeCurrency === prevRouteCurrencyRef.current) {
      return
    }

    prevRouteCurrencyRef.current = routeCurrency
    setCurrency(routeCurrency)
  }, [routeCurrency, setCurrency])

  return routeCurrency
}
